import { useEffect, useState, useCallback } from "react";
import { useAdmin } from "../contexts/AdminContext";
import { ErrorBanner } from "../components/ErrorBanner";
import { formatDate, formatKes } from "../lib/utils";
import type {
  RenewalForecast,
  ShopSubscription,
  SubscriptionAdminStats,
} from "../lib/types";

const PAGE_SIZE = 50n;

type SyncStatus = "idle" | "loading" | "ok" | "error";

export function SubscriptionsPage() {
  const { actor } = useAdmin();
  const [items, setItems] = useState<ShopSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [syncState, setSyncState] = useState<Record<string, SyncStatus>>({});
  const [stats, setStats] = useState<SubscriptionAdminStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState("");

  const fetchPage = useCallback(async (cursor: string | null) => {
    if (!actor) return;
    setLoading(true);
    setError("");
    try {
      const res: { ok?: { items: ShopSubscription[]; nextCursor: [] | [string] }; err?: unknown } =
        await actor.listSubscriptions(cursor ? [cursor] : [], PAGE_SIZE);
      if ("ok" in res) {
        setItems(res.ok!.items);
        setNextCursor(res.ok!.nextCursor[0] ?? null);
      } else {
        setError("Failed to load subscriptions");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [actor]);

  const fetchStats = useCallback(async () => {
    if (!actor) return;
    setStatsLoading(true);
    setStatsError("");
    try {
      const res = await actor.getSubscriptionAdminStats() as {
        ok?: SubscriptionAdminStats;
        err?: unknown;
      };
      if ("ok" in res && res.ok) {
        setStats(res.ok);
      } else {
        setStatsError("Subscription metrics are unavailable.");
      }
    } catch {
      setStatsError("Subscription metrics are unavailable.");
    } finally {
      setStatsLoading(false);
    }
  }, [actor]);

  useEffect(() => {
    fetchPage(null);
    fetchStats();
  }, [fetchPage, fetchStats]);

  function nextPage() {
    if (!nextCursor) return;
    setCursorStack((s) => [...s, nextCursor]);
    fetchPage(nextCursor);
  }

  function prevPage() {
    if (cursorStack.length <= 1) return;
    const newStack = cursorStack.slice(0, -1);
    setCursorStack(newStack);
    fetchPage(newStack[newStack.length - 1]);
  }

  async function syncSubscription(subId: bigint) {
    if (!actor) return;
    const key = String(subId);
    setSyncState((s) => ({ ...s, [key]: "loading" }));
    try {
      const res: { ok?: null; err?: unknown } = await actor.syncSubscriptionToShop(subId);
      if ("ok" in res) {
        setSyncState((s) => ({ ...s, [key]: "ok" }));
        setTimeout(() => setSyncState((s) => ({ ...s, [key]: "idle" })), 3000);
      } else {
        setSyncState((s) => ({ ...s, [key]: "error" }));
      }
    } catch {
      setSyncState((s) => ({ ...s, [key]: "error" }));
    }
  }

  const hasPrev = cursorStack.length > 1;

  return (
    <div className="subscriptions-page">
      <header className="subscriptions-head">
        <h1>Subscriptions</h1>
        <p>Current coverage and upcoming renewal potential.</p>
      </header>

      <section className="subscription-metrics" aria-labelledby="coverage-heading">
        <h2 id="coverage-heading">Coverage</h2>
        <div className="coverage-grid">
          <CoverageCard
            label="Subscribed shops"
            value={statsLoading ? null : stats?.subscribedShops ?? null}
          />
          <CoverageCard
            label="Active"
            value={statsLoading ? null : stats?.activeShops ?? null}
            tone="active"
          />
          <CoverageCard
            label="Expired"
            value={statsLoading ? null : stats?.expiredShops ?? null}
            tone="expired"
          />
        </div>
      </section>

      <section className="subscription-metrics renewal-metrics" aria-labelledby="renewal-heading">
        <div className="metric-heading-row">
          <h2 id="renewal-heading">Renewal forecast</h2>
          <p>
            Cumulative · current monthly tier pricing
            {stats ? ` · as of ${formatDate(stats.asOf)}` : ""}
          </p>
        </div>
        <div className="renewal-grid">
          <RenewalCard
            label="Next 7 days"
            forecast={statsLoading ? null : stats?.renewals7Days ?? null}
            loading={statsLoading}
          />
          <RenewalCard
            label="Next 14 days"
            forecast={statsLoading ? null : stats?.renewals14Days ?? null}
            loading={statsLoading}
          />
          <RenewalCard
            label="Next 30 days"
            forecast={statsLoading ? null : stats?.renewals30Days ?? null}
            loading={statsLoading}
          />
        </div>
      </section>

      {statsError && (
        <div className="subscription-stats-error" role="alert">
          <span>{statsError}</span>
          <button className="btn-ghost" onClick={fetchStats}>Retry</button>
        </div>
      )}

      {error && <ErrorBanner message={error} onDismiss={() => setError("")} />}

      {loading ? (
        <p style={{ color: "var(--color-muted)" }}>Loading…</p>
      ) : items.length === 0 ? (
        <p style={{ color: "var(--color-muted)" }}>No subscriptions yet.</p>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Shop</th>
                <th>Started</th>
                <th>Expires</th>
                <th>Duration</th>
                <th>Via</th>
                <th>Details</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((sub) => {
                const via: "free-code" | "mpesa" | "free-trial" =
                  sub.subscriptionCode[0] != null ? "free-code"
                  : sub.mpesaRef[0] != null ? "mpesa"
                  : "free-trial";
                const detail = sub.subscriptionCode[0] ?? sub.mpesaRef[0];
                const key = String(sub.id);
                const status = syncState[key] ?? "idle";
                return (
                  <tr key={key}>
                    <td className="mono" style={{ color: "var(--color-muted)" }}>
                      {String(sub.id)}
                    </td>
                    <td className="mono" style={{ fontWeight: 500 }}>
                      TF{String(sub.shopNumber)}
                    </td>
                    <td style={{ color: "var(--color-muted)", fontSize: 13, whiteSpace: "nowrap" }}>
                      {formatDate(sub.started)}
                    </td>
                    <td style={{ color: "var(--color-muted)", fontSize: 13, whiteSpace: "nowrap" }}>
                      {formatDate(sub.expires)}
                    </td>
                    <td>
                      <DurationBadge
                        durationDays={sub.durationDays}
                        expires={sub.expires}
                      />
                    </td>
                    <td>
                      <ViaBadge via={via} />
                    </td>
                    <td>
                      {detail ? (
                        <span className="mono" style={{ fontSize: 12, color: "var(--color-muted)" }}>
                          {detail}
                        </span>
                      ) : (
                        <span style={{ color: "var(--color-muted)", fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td>
                      <SyncButton
                        status={status}
                        onClick={() => syncSubscription(sub.id)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(hasPrev || nextCursor) && (
        <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
          <button onClick={prevPage} disabled={!hasPrev} style={pageBtnStyle(!hasPrev)}>
            ← Prev
          </button>
          <button onClick={nextPage} disabled={!nextCursor} style={pageBtnStyle(!nextCursor)}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

function CoverageCard({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: bigint | null;
  tone?: "neutral" | "active" | "expired";
}) {
  return (
    <div className={`coverage-card ${tone}`}>
      <span>{label}</span>
      <strong className="mono">{value === null ? "—" : String(value)}</strong>
    </div>
  );
}

function RenewalCard({
  label,
  forecast,
  loading,
}: {
  label: string;
  forecast: RenewalForecast | null;
  loading: boolean;
}) {
  const shopCount = forecast?.shops ?? null;
  return (
    <div className="renewal-card">
      <span>{label}</span>
      <strong className="mono">
        {forecast === null ? "—" : formatKes(forecast.amountKes)}
      </strong>
      <small>
        {shopCount === null
          ? loading ? "Loading" : "Unavailable"
          : `${String(shopCount)} ${shopCount === 1n ? "shop" : "shops"}`}
      </small>
    </div>
  );
}

function SyncButton({ status, onClick }: { status: SyncStatus; onClick: () => void }) {
  const isLoading = status === "loading";
  const isOk = status === "ok";
  const isError = status === "error";

  const color = isOk
    ? "var(--color-accent)"
    : isError
    ? "#f87171"
    : "var(--color-muted)";

  const label = isLoading ? "…" : isOk ? "✓" : isError ? "!" : "Sync";
  const title = isError ? "Sync failed — try again" : isOk ? "Synced" : "Push subscription to shop canister";

  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      title={title}
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: "3px 10px",
        borderRadius: 4,
        border: `1px solid ${isOk ? "rgba(0,232,122,0.3)" : isError ? "rgba(248,113,113,0.3)" : "var(--color-border)"}`,
        background: isOk ? "rgba(0,232,122,0.08)" : isError ? "rgba(248,113,113,0.08)" : "transparent",
        color,
        cursor: isLoading ? "not-allowed" : "pointer",
        opacity: isLoading ? 0.6 : 1,
        transition: "color 0.15s, background 0.15s, border-color 0.15s",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );
}

type ExpiryTone = "expired" | "upcoming" | "healthy";

const DAY_MS = 86_400_000;
const EAT_OFFSET_MS = 3 * 60 * 60 * 1_000;

function expiryTone(expires: bigint): ExpiryTone {
  const nowMs = Date.now();
  const expiresMs = Number(expires / 1_000_000n);
  if (expiresMs <= nowMs) return "expired";
  const todayEat = Math.floor((nowMs + EAT_OFFSET_MS) / DAY_MS);
  const expiryDayEat = Math.floor((expiresMs + EAT_OFFSET_MS) / DAY_MS);
  return expiryDayEat - todayEat <= 7 ? "upcoming" : "healthy";
}

const durationBadgeStyles: Record<
  ExpiryTone,
  { background: string; color: string; border: string; title: string }
> = {
  expired: {
    background: "rgba(138,158,149,0.08)",
    color: "var(--color-muted)",
    border: "1px solid rgba(138,158,149,0.28)",
    title: "Expired",
  },
  upcoming: {
    background: "rgba(245,200,66,0.1)",
    color: "var(--color-yellow)",
    border: "1px solid rgba(245,200,66,0.42)",
    title: "Expires within 7 days",
  },
  healthy: {
    background: "rgba(0,232,122,0.07)",
    color: "var(--color-accent)",
    border: "1px solid rgba(0,232,122,0.25)",
    title: "Active beyond 7 days",
  },
};

function DurationBadge({
  durationDays,
  expires,
}: {
  durationDays: bigint;
  expires: bigint;
}) {
  const style = durationBadgeStyles[expiryTone(expires)];
  return (
    <span
      className="mono"
      title={style.title}
      style={{
        display: "inline-block",
        padding: "3px 8px",
        borderRadius: 4,
        background: style.background,
        color: style.color,
        border: style.border,
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {String(durationDays)}d
    </span>
  );
}

const viaBadgeStyles: Record<string, { background: string; color: string; border: string; label: string }> = {
  "free-code":  { background: "rgba(0,232,122,0.12)",  color: "var(--color-accent)", border: "1px solid rgba(0,232,122,0.3)",  label: "Free Code" },
  "mpesa":      { background: "rgba(59,130,246,0.12)", color: "#60a5fa",             border: "1px solid rgba(59,130,246,0.3)", label: "M-Pesa" },
  "free-trial": { background: "rgba(168,85,247,0.12)", color: "#c084fc",             border: "1px solid rgba(168,85,247,0.3)", label: "Free Trial" },
};

function ViaBadge({ via }: { via: "free-code" | "mpesa" | "free-trial" }) {
  const s = viaBadgeStyles[via];
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 4,
      background: s.background, color: s.color, border: s.border }}>
      {s.label}
    </span>
  );
}

function pageBtnStyle(disabled: boolean | null): React.CSSProperties {
  return {
    padding: "8px 16px",
    borderRadius: 4,
    border: "1px solid var(--color-border)",
    background: "var(--color-surface)",
    color: disabled ? "var(--color-muted)" : "var(--color-text)",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 13,
    opacity: disabled ? 0.5 : 1,
  };
}
